// Configurable via VITE_* env vars (see .env.example) — same convention as
// VITE_API_URL in src/lib/apiClient.ts — so these can be tuned per
// environment (e.g. a slower cadence against a rate-limited dev tunnel)
// without a code change. Falls back to the given default whenever the env
// var is unset, empty, or not a positive number.
function readPollIntervalMs(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw);
  return raw !== undefined && raw !== "" && Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

// How often UploadHistoryContext's pollUploadStatus checks a processing
// upload's coarse status/stage/progress.
export const UPLOAD_STATUS_POLL_MS = readPollIntervalMs(import.meta.env.VITE_UPLOAD_STATUS_POLL_MS, 3000);

// How often UploadDetailPanel re-fetches this upload's records while it's
// still "processing". 2500ms, not a faster round number: the backend
// converts pages in concurrent batches (GROQ_PARALLEL_PAGES, default 4) via
// an LLM call per page, and observed page-to-page completion gaps within a
// batch ranged ~0.2s-2.6s (mean ~1.3s) in live testing — a real page simply
// cannot commit new records faster than an LLM round-trip allows. Polling
// much faster than that (1000ms was tried and reliably tripped ngrok tunnel
// rate limiting/connection resets under combined load with the status poll)
// buys no earlier visibility into data that doesn't exist yet; 2500ms still
// samples every batch multiple times over a longer document's conversion
// window while roughly halving sustained request volume versus the previous
// 1500ms default.
export const RECORDS_POLL_MS = readPollIntervalMs(import.meta.env.VITE_RECORDS_POLL_MS, 2500);

// Ceiling for the exponential backoff both polls apply after consecutive
// failures (see src/shared/lib/pollingBackoff.ts) — 30s caps how stale the
// view can get during a real outage while still giving a struggling
// connection real room to recover between attempts.
export const POLL_BACKOFF_MAX_MS = readPollIntervalMs(import.meta.env.VITE_POLL_BACKOFF_MAX_MS, 30_000);

// Default interval for useLiveRefreshFallback (src/shared/hooks/) — the
// session-wide safety-net poll for NotificationsMenu and FamilyContext,
// which currently do 100% of the "live update" work since the realtime
// socket is disabled (see src/lib/socketClient.ts). Not upload-specific,
// but shares this config module since it contributes to the same total
// request volume against the same backend/tunnel.
export const LIVE_REFRESH_FALLBACK_MS = readPollIntervalMs(import.meta.env.VITE_LIVE_REFRESH_FALLBACK_MS, 60_000);

// Minimum gap between focus/visibilitychange-triggered refetches in
// useLiveRefreshFallback. Without this, every native file-picker dialog
// opening or closing during an upload blurs then refocuses the tab, firing
// an immediate refetch from EVERY mounted useLiveRefreshFallback caller —
// which is what turned four unrelated endpoints (notifications, family
// dependents/caregivers/access-requests) into dozens of requests during a
// single upload, none of them actually about the upload. A genuine "away
// for a while, came back" case still refetches immediately — this only
// collapses focus events that arrive faster than a real away-and-back
// could plausibly happen.
export const LIVE_REFRESH_MIN_REFOCUS_GAP_MS = readPollIntervalMs(import.meta.env.VITE_LIVE_REFRESH_MIN_REFOCUS_GAP_MS, 15_000);
